// Admin "Customers" screen (organizations) — new admin nav item. Same
// TanStack Query + apiClient pattern as every other admin hook file (see
// useAdminCatalog.js's own header comment) — merge-then-invalidate on a
// single-row mutation, plain invalidate-and-refetch on a bulk one.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const LIST_KEY_PREFIX = ['admin', 'customers'];
const listKey = (search, sortBy, sortDir) => [
  ...LIST_KEY_PREFIX,
  search ?? null,
  sortBy ?? null,
  sortDir ?? null,
];
const detailKey = (id) => ['admin', 'customerDetail', id ?? null];

export function useAdminCustomers({ search, sortBy, sortDir } = {}) {
  return useQuery({
    queryKey: listKey(search, sortBy, sortDir),
    queryFn: () => apiClient.get('/admin/organizations', { search, sortBy, sortDir }),
    select: (data) => data.organizations,
  });
}

export function useAdminCustomerDetail(id) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: () => apiClient.get(`/admin/organizations/${id}`),
    enabled: Boolean(id),
  });
}

function invalidateCustomers(queryClient, id) {
  queryClient.invalidateQueries({ queryKey: LIST_KEY_PREFIX });
  if (id) queryClient.invalidateQueries({ queryKey: detailKey(id) });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => apiClient.patch(`/admin/organizations/${id}`, body),
    onSuccess: (_data, variables) => invalidateCustomers(queryClient, variables.id),
  });
}

export function useDeactivateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/admin/organizations/${id}/deactivate`),
    onSuccess: (_data, id) => invalidateCustomers(queryClient, id),
  });
}

export function useReactivateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/admin/organizations/${id}/reactivate`),
    onSuccess: (_data, id) => invalidateCustomers(queryClient, id),
  });
}

// DELETE /admin/organizations/:id — deleteOrDeactivateOrganization decides
// server-side which of the two actually happened; the response's `outcome`
// field tells the caller which toast to show.
export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/organizations/${id}`),
    onSuccess: (_data, id) => invalidateCustomers(queryClient, id),
  });
}

// --- Bulk actions (multi-select task) -------------------------------------

export function useBulkDeactivateCustomers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => apiClient.post('/admin/organizations/bulk-deactivate', { ids }),
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

export function useBulkReactivateCustomers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => apiClient.post('/admin/organizations/bulk-reactivate', { ids }),
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

export function useBulkDeleteCustomers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, confirmedCount }) =>
      apiClient.post('/admin/organizations/bulk-delete', { ids, confirmedCount }),
    onSuccess: () => invalidateCustomers(queryClient),
  });
}

// GET /admin/organizations/bulk-delete-preview?ids=1,2,3 — called
// imperatively right before the confirm dialog opens, see
// utils/bulkActions.js#confirmAndBulkDelete.
export function fetchCustomerBulkDeletePreview(ids) {
  return apiClient.get('/admin/organizations/bulk-delete-preview', { ids: ids.join(',') });
}

export default useAdminCustomers;
